import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { BoardJournal } from '../../../../stores/boardStore';
import { SafeImage } from '../../../../components/common/SafeImage';
import { AnimatedLogo } from '../../../../components/common/AnimatedLogo';
import { BoardJournalDetails } from '../BoardJournalDetails';

interface BoardJournalDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    boardId: string;
    selectedJournal: BoardJournal | null;
    isCreating?: boolean;
    onOpenInFullView?: () => void;
    onOperationCreated?: (opId: string) => void;
}

export const BoardJournalDetailsModal: React.FC<BoardJournalDetailsModalProps> = ({
    isOpen,
    onClose,
    boardId,
    selectedJournal,
    isCreating = false,
    onOpenInFullView,
    onOperationCreated
}) => {
    const { t } = useTranslation('common');

    const CDN_URL = (((window as unknown as { __DASHBAL_CONFIG__?: { cdnUrl?: string } }).__DASHBAL_CONFIG__?.cdnUrl) || 'https://cdn.dashbal.com').replace(/\/$/, '');

    return (
        <Transition appear show={isOpen && !!selectedJournal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 rounded-2xl shadow-2xl w-full max-w-[896px] flex flex-col overflow-hidden transform h-[85vh] max-h-[85vh]">
                                {selectedJournal && (
                                    <>
                                        {/* Header */}
                                        <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 z-10">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <button
                                                        onClick={onClose}
                                                        aria-label={t('actions.back', 'Back') as string}
                                                        className="flex-shrink-0 p-1.5 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-1"
                                                    >
                                                        <span
                                                            className="w-5 h-5"
                                                            style={{
                                                                maskImage: `url(https://cdn.dashbal.com/assets/icon_919272a5.svg)`,
                                                                WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_919272a5.svg)`,
                                                                maskRepeat: 'no-repeat',
                                                                WebkitMaskRepeat: 'no-repeat',
                                                                maskPosition: 'center',
                                                                WebkitMaskPosition: 'center',
                                                                maskSize: 'contain',
                                                                WebkitMaskSize: 'contain',
                                                                backgroundColor: 'currentColor',
                                                                display: 'inline-block'
                                                            }}
                                                            role="img"
                                                        />
                                                    </button>
                                                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 overflow-hidden shadow-sm">
                                                        <SafeImage src={`${CDN_URL}/logos/internal/money_journal.svg`} className="w-6 h-6 object-contain" />
                                                    </div>
                                                    <h2
                                                        className="text-base font-bold text-neutral-700 dark:text-neutral-100 truncate"
                                                        title={selectedJournal.name}
                                                    >
                                                        {selectedJournal.name}
                                                    </h2>
                                                </div>

                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {onOpenInFullView && !isCreating && (
                                                        <button
                                                            onClick={onOpenInFullView}
                                                            className="px-3 py-1.5 text-xs font-medium bg-accent-primary text-white rounded-md hover:bg-accent-primary/90 transition-colors shadow-sm"
                                                        >
                                                            {t('boardJournals.inBoardModal.openInFullView', 'Open in full view')}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={onClose}
                                                        aria-label={t('common.close', 'Close') as string}
                                                        className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors focus:outline-none"
                                                    >
                                                        <span
                                                            className="w-5 h-5 text-neutral-500 dark:text-neutral-400"
                                                            style={{
                                                                maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`,
                                                                WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`,
                                                                maskRepeat: 'no-repeat',
                                                                WebkitMaskRepeat: 'no-repeat',
                                                                maskPosition: 'center',
                                                                WebkitMaskPosition: 'center',
                                                                maskSize: 'contain',
                                                                WebkitMaskSize: 'contain',
                                                                backgroundColor: 'currentColor',
                                                                display: 'inline-block'
                                                            }}
                                                            role="img"
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        {isCreating ? (
                                            <div className="flex-1 min-h-0 overflow-hidden flex flex-col items-center justify-center px-6 py-10 gap-4">
                                                <AnimatedLogo variant="processing" size="md" />
                                                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                                    {t('boardJournals.inBoardModal.creating', 'Creating journal...')}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex-1 min-h-0 overflow-hidden">
                                                <BoardJournalDetails
                                                    boardId={boardId}
                                                    selectedJournal={selectedJournal}
                                                    minimalMode
                                                    hideBackButton
                                                    hideHeader
                                                    onOperationCreated={onOperationCreated}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
